import React, { useEffect, useState } from 'react';
import { Wand2, Trash2, Image as ImgIcon, RefreshCw, Video, Sparkles, Send, GitCompare, Check, Trophy } from 'lucide-react';
import { toast } from 'sonner';
import api, { formatApiError } from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { BoltLoader, ZapBolt } from '@/components/BoostaBolt';
import { PublishDialog } from '@/components/PublishDialog';
import { CompareDialog } from '@/components/CompareDialog';
import { UpgradeLock } from '@/components/UpgradeLock';
import { NeonSelect } from '@/components/NeonSelect';

const STYLES = ['premium', 'cinematic', 'minimal', 'editorial', 'vibrant'];
const ASPECTS = ['1:1', '4:5', '16:9', '9:16'];

export default function Creatives() {
  const { user, refreshUser } = useAuth();
  const [tab, setTab] = useState('image');
  const [prompt, setPrompt] = useState('');
  const [style, setStyle] = useState('premium');
  const [aspect, setAspect] = useState('1:1');
  const [busy, setBusy] = useState(false);
  const [refining, setRefining] = useState(false);
  const [regenId, setRegenId] = useState(null);
  const [items, setItems] = useState([]);
  // Video tab state
  const [videoPrompt, setVideoPrompt] = useState('');
  const [videoAspect, setVideoAspect] = useState('16:9');
  const [videoBusy, setVideoBusy] = useState(false);
  const [publishTarget, setPublishTarget] = useState(null);
  const [compareMode, setCompareMode] = useState(false);
  const [selected, setSelected] = useState([]);
  const [compareOpen, setCompareOpen] = useState(false);
  const polling = React.useRef({});

  const pollVideo = (id) => {
    if (polling.current[id]) return;
    polling.current[id] = setInterval(async () => {
      try {
        const { data } = await api.get(`/creatives/video/${id}/status`);
        if (data.status === 'ready' || data.status === 'failed') {
          clearInterval(polling.current[id]);
          delete polling.current[id];
          await load();
        }
      } catch (e) { console.error('Creative poll failed', e); }
    }, 5000);
  };

  const refinePrompt = async () => {
    if (!prompt.trim()) { toast.error('Type a brief first.'); return; }
    setRefining(true);
    try {
      const { data } = await api.post('/ai/refine-prompt', { prompt });
      setPrompt(data.refined);
      toast.success('Prompt refined by AI director.');
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setRefining(false); }
  };

  const load = async () => {
    const { data } = await api.get('/creatives');
    setItems(data);
    data.filter(c => c.type === 'video' && c.status === 'processing').forEach(c => pollVideo(c.creative_id));
  };
  useEffect(() => {
    load();
    const timers = polling.current;
    return () => { Object.values(timers).forEach(clearInterval); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const generateVideo = async () => {
    if (!videoPrompt.trim()) { toast.error('Enter a video ad brief.'); return; }
    setVideoBusy(true);
    try {
      const { data } = await api.post('/creatives/video', { prompt: videoPrompt, aspect: videoAspect });
      toast.success('Video ad queued. 100 ZAP used — rendering ~6s clip.');
      setVideoPrompt('');
      await Promise.all([load(), refreshUser()]);
      pollVideo(data.creative_id);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setVideoBusy(false); }
  };

  const regenerateVideo = async (id) => {
    setRegenId(id);
    try {
      const { data } = await api.post(`/creatives/video/${id}/regenerate`);
      toast.success('Regenerating video. 60 ZAP used.');
      await Promise.all([load(), refreshUser()]);
      pollVideo(data.creative_id);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setRegenId(null); }
  };

  const generate = async () => {
    if (!prompt.trim()) { toast.error('Enter an ad brief.'); return; }
    setBusy(true);
    try {
      await api.post('/ai/creative', { prompt, style, aspect });
      toast.success('Ad creative generated. 25 ZAP used.');
      setPrompt('');
      await Promise.all([load(), refreshUser()]);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const regenerate = async (id) => {
    setRegenId(id);
    try {
      await api.post(`/ai/creative/${id}/regenerate`);
      toast.success('Regenerated. 15 ZAP used.');
      await Promise.all([load(), refreshUser()]);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setRegenId(null); }
  };

  const del = async (id) => {
    if (!window.confirm('Delete this creative?')) return;
    await api.delete(`/creatives/${id}`); await load();
  };

  const isSelectable = (c) => c.type !== 'video' || (c.status === 'ready' && c.video_url);
  const toggleCompare = () => {
    setCompareMode(m => !m);
    setSelected([]);
  };
  const toggleSelect = (c) => {
    if (!isSelectable(c)) { toast.error('This creative is still rendering.'); return; }
    setSelected(prev => {
      if (prev.find(x => x.creative_id === c.creative_id)) return prev.filter(x => x.creative_id !== c.creative_id);
      if (prev.length >= 2) { toast.error('Pick exactly two to compare.'); return prev; }
      return [...prev, c];
    });
  };
  const pickWinner = async (winner) => {
    const loser = selected.find(x => x.creative_id !== winner.creative_id);
    try {
      await api.post('/creatives/compare', { winner_id: winner.creative_id, loser_id: loser?.creative_id });
    } catch (e) { console.error('Record compare failed', e); }
    setCompareOpen(false);
    setCompareMode(false);
    setSelected([]);
    setPublishTarget(winner);
    await load();
    toast.success('Winner selected — publish it now.');
  };
  const selectedIds = selected.map(s => s.creative_id);

  return (
    <div className="space-y-7" data-testid="creatives-root">
      <div>
        <div className="text-[10px] tracking-[0.25em] uppercase font-bold text-boosta-cyan mb-2">Creative Studio</div>
        <h1 className="font-display font-black text-4xl tracking-tighter text-3d">Generate ad-ready creatives</h1>
        <p className="text-white/55 text-sm mt-1">Composed as advertisements — hero product focus, promotional hierarchy, photoreal fidelity.</p>
      </div>

      <div className="flex gap-2 border-b border-white/5">
        {[
          { id: 'image', label: 'Image ad', cost: '25 ZAP · Regen 15' },
          { id: 'video', label: 'Video ad', cost: '100 ZAP · Regen 60' },
        ].map(t => (
          <button key={t.id} onClick={()=>setTab(t.id)} data-testid={`tab-${t.id}`}
            className={`px-4 py-3 text-sm font-medium relative ${tab===t.id?'text-white':'text-white/45 hover:text-white/70'}`}>
            <span className="inline-flex items-center gap-2">{t.id==='video' ? <Video className="w-4 h-4"/> : <ImgIcon className="w-4 h-4"/>}{t.label}</span>
            <span className="ml-3 text-[10px] uppercase tracking-wider text-boosta-cyan/70">{t.cost}</span>
            {t.soon && <span className="ml-2 text-[10px] px-2 py-0.5 rounded-full bg-boosta-yellow/15 text-boosta-yellow">Soon</span>}
            {tab===t.id && <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-boosta-cyan"/>}
          </button>
        ))}
      </div>

      {tab==='image' && (
        <div className="glass rounded-2xl p-6">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-4">
            <div className="flex flex-wrap gap-2 mb-3">
              {[
                { name: 'Skincare', brief: "Premium skincare serum bottle on wet black marble, soft golden hour light, dewy droplets, luxury editorial feel, empty top-right third for headline" },
                { name: 'Fitness', brief: "Athlete mid-rep in a moody industrial gym, dramatic rim lighting, chalk dust in the air, energy and intensity, negative space at top for bold headline" },
                { name: 'SaaS', brief: "Sleek laptop showing a glowing analytics dashboard on a dark desk, cinematic depth of field, blue neon accents, clean space on the left for copy" },
                { name: 'Restaurant', brief: "Overhead shot of a vibrant signature dish on dark slate, steam rising, warm candlelight, appetising editorial food photography, space at bottom for offer text" },
                { name: 'Fashion', brief: "Model in a statement jacket against a bold single-colour backdrop, high-fashion studio lighting, confident pose, magazine cover composition with headline space top-left" },
                { name: 'Real estate', brief: "Modern living room at dusk with floor-to-ceiling windows and warm interior glow, aspirational lifestyle framing, clear sky area at top for headline" },
              ].map(t => (
                <button key={t.name} type="button" onClick={() => setPrompt(t.brief)}
                  className="text-[11px] px-3 py-1.5 rounded-full border border-white/10 bg-black/25 text-white/60 hover:border-boosta-cyan/50 hover:text-white transition-colors"
                  data-testid={`template-${t.name.toLowerCase().replace(' ', '-')}`}>
                  {t.name}
                </button>
              ))}
            </div>
            <textarea data-testid="creative-prompt" value={prompt} onChange={e=>setPrompt(e.target.value)} rows="3"
              placeholder="Ad brief: e.g. 'Premium skincare bottle on marble, golden light, luxury magazine cover feel, empty top-right for headline'…"
              className="md:col-span-8 bg-black/30 border border-white/10 rounded-xl px-4 py-3 text-sm focus:border-boosta-cyan/50 outline-none resize-none"/>
            <div className="md:col-span-4 grid grid-cols-2 gap-3">
              <div>
                <label className="text-[10px] uppercase tracking-wider text-white/45 mb-1.5 block">Style</label>
                <NeonSelect value={style} onChange={e=>setStyle(e.target.value)} data-testid="creative-style"
                  options={STYLES.map(s=>({value:s,label:s}))}/>
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-wider text-white/45 mb-1.5 block">Aspect</label>
                <NeonSelect value={aspect} onChange={e=>setAspect(e.target.value)} data-testid="creative-aspect"
                  options={ASPECTS.map(a=>({value:a,label:a}))}/>
              </div>
              <button disabled={busy} onClick={generate} data-testid="creative-generate" className="col-span-2 btn-primary rounded-xl py-3 text-sm font-bold inline-flex items-center justify-center gap-2 disabled:opacity-50">
                {busy ? <BoltLoader size={22} label="Composing ad"/> : <><Wand2 className="w-4 h-4"/> Generate ad · <ZapBolt size={16}/> 25</>}
              </button>
              <button disabled={refining || !prompt.trim()} onClick={refinePrompt} data-testid="creative-refine" className="col-span-2 btn-ghost rounded-xl py-2 text-xs inline-flex items-center justify-center gap-2 disabled:opacity-40">
                <Sparkles className="w-3.5 h-3.5"/> {refining ? 'Refining brief…' : 'Refine brief with AI director (free)'}
              </button>
            </div>
          </div>
        </div>
      )}

      {tab==='video' && (
        !user?.features?.video ? (
          <UpgradeLock title="AI video ads" plan="Pro" testid="video-upgrade-lock"/>
        ) : (
        <div className="glass rounded-2xl p-6" data-testid="video-panel">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-4">
            <textarea data-testid="video-prompt" value={videoPrompt} onChange={e=>setVideoPrompt(e.target.value)} rows="3"
              placeholder="Video ad brief: e.g. 'Sleek wireless earbud rotating in mid-air, neon reflections, slow camera orbit, dark studio, cinematic depth of field'…"
              className="md:col-span-8 bg-black/30 border border-white/10 rounded-xl px-4 py-3 text-sm focus:border-boosta-cyan/50 outline-none resize-none"/>
            <div className="md:col-span-4 grid grid-cols-2 gap-3">
              <div className="col-span-2">
                <label className="text-[10px] uppercase tracking-wider text-white/45 mb-1.5 block">Aspect</label>
                <NeonSelect value={videoAspect} onChange={e=>setVideoAspect(e.target.value)} data-testid="video-aspect"
                  options={[{value:'16:9',label:'16:9 · Landscape'},{value:'9:16',label:'9:16 · Vertical'}]}/>
              </div>
              <button disabled={videoBusy} onClick={generateVideo} data-testid="video-generate" className="col-span-2 btn-primary rounded-xl py-3 text-sm font-bold inline-flex items-center justify-center gap-2 disabled:opacity-50">
                {videoBusy ? <BoltLoader size={22} label="Queuing video"/> : <><Video className="w-4 h-4"/> Generate video · <ZapBolt size={16}/> 100</>}
              </button>
              <div className="col-span-2 text-[11px] text-white/45 leading-relaxed">~6s cinematic clip · rendered async, appears below when ready. Regenerate costs 60 ZAP.</div>
            </div>
          </div>
        </div>
        )
      )}

      <div>
        <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
          <div className="font-display font-bold text-xl">Recent creatives</div>
          {items.length >= 2 && (
            <button onClick={toggleCompare} data-testid="compare-toggle"
              className={`rounded-full px-4 py-2 text-sm inline-flex items-center gap-2 transition ${compareMode ? 'btn-primary' : 'btn-ghost'}`}>
              <GitCompare className="w-4 h-4"/> {compareMode ? 'Cancel compare' : 'Compare A/B'}
            </button>
          )}
        </div>
        {compareMode && (
          <div className="mb-4 text-xs text-boosta-cyan bg-boosta-cyan/10 border border-boosta-cyan/20 rounded-xl px-4 py-2.5" data-testid="compare-hint">
            Select <b>two</b> creatives to compare side by side, then pick the winner to publish. ({selected.length}/2 selected)
          </div>
        )}
        {items.length === 0 ? (
          <div className="glass rounded-2xl py-16 text-center">
            <ImgIcon className="w-12 h-12 mx-auto mb-3 text-white/20"/>
            <div className="text-white/55 text-sm">No creatives yet — generate your first ad.</div>
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {items.map(c => (
              <div key={c.creative_id}
                onClick={compareMode ? ()=>toggleSelect(c) : undefined}
                className={`glass glass-hover rounded-2xl overflow-hidden group transition ${compareMode ? 'cursor-pointer' : ''} ${selectedIds.includes(c.creative_id) ? 'ring-2 ring-boosta-cyan' : ''} ${compareMode && !isSelectable(c) ? 'opacity-50' : ''}`}
                data-testid={`creative-${c.creative_id}`}>
                <div className="aspect-square bg-black/40 relative overflow-hidden flex items-center justify-center">
                  {compareMode && (
                    <span className={`absolute top-2 right-2 z-10 w-6 h-6 rounded-full flex items-center justify-center border-2 ${selectedIds.includes(c.creative_id) ? 'bg-boosta-cyan border-boosta-cyan text-boosta-ink' : 'bg-black/50 border-white/40 text-transparent'}`} data-testid={`compare-select-${c.creative_id}`}>
                      <Check className="w-3.5 h-3.5"/>
                    </span>
                  )}
                  {c.type === 'video' ? (
                    c.status === 'ready' && c.video_url ? (
                      <video src={c.video_url} controls loop muted playsInline className="w-full h-full object-cover" data-testid={`video-player-${c.creative_id}`}/>
                    ) : c.status === 'failed' ? (
                      <div className="text-center px-3" data-testid={`video-failed-${c.creative_id}`}>
                        <Video className="w-8 h-8 mx-auto mb-2 text-red-400/60"/>
                        <div className="text-[11px] text-red-400/80">Render failed</div>
                      </div>
                    ) : (
                      <div className="text-center px-3" data-testid={`video-processing-${c.creative_id}`}>
                        <BoltLoader size={26} label=""/>
                        <div className="text-[11px] text-boosta-cyan mt-2">Rendering clip…</div>
                      </div>
                    )
                  ) : (
                    <img src={c.image_url} alt={c.prompt} className="w-full h-full object-cover transition group-hover:scale-105"/>
                  )}
                  {c.type === 'video' && <span className="absolute top-2 left-2 text-[9px] px-2 py-0.5 rounded-full bg-boosta-yellow/20 text-boosta-yellow uppercase tracking-wider font-bold inline-flex items-center gap-1"><Video className="w-2.5 h-2.5"/>Video</span>}
                  {c.parent_id && c.type !== 'video' && <span className="absolute top-2 left-2 text-[9px] px-2 py-0.5 rounded-full bg-boosta-cyan/20 text-boosta-cyan uppercase tracking-wider">Regen</span>}
                  {!c.parent_id && c.regen_count > 0 && <span className="absolute top-2 right-2 text-[9px] px-2 py-0.5 rounded-full bg-boosta-yellow/20 text-boosta-yellow uppercase tracking-wider font-bold" data-testid={`regen-count-${c.creative_id}`}>+{c.regen_count} regen</span>}
                  {c.ab_winner && <span className="absolute bottom-2 left-2 text-[9px] px-2 py-0.5 rounded-full bg-boosta-emerald/20 text-boosta-emerald border border-boosta-emerald/30 uppercase tracking-wider font-bold inline-flex items-center gap-1" data-testid={`winner-badge-${c.creative_id}`}><Trophy className="w-2.5 h-2.5"/> Winner</span>}
                </div>
                <div className="p-3">
                  <div className="text-xs text-white/65 line-clamp-2 mb-2">{c.prompt}</div>
                  <div className="flex justify-between items-center">
                    <span className="text-[10px] uppercase tracking-wider text-boosta-cyan">{c.type === 'video' ? c.aspect : c.style}</span>
                    {!compareMode && (
                    <div className="flex gap-2">
                      <button disabled={c.type==='video' && c.status!=='ready'} onClick={()=>setPublishTarget(c)} title="Post to social" className="text-white/50 hover:text-boosta-cyan disabled:opacity-30" data-testid={`creative-publish-${c.creative_id}`}>
                        <Send className="w-3.5 h-3.5"/>
                      </button>
                      <button disabled={regenId===c.creative_id || (c.type==='video' && c.status==='processing')} onClick={()=> c.type==='video' ? regenerateVideo(c.creative_id) : regenerate(c.creative_id)} title={c.type==='video' ? 'Regenerate (60 ZAP)' : 'Regenerate (15 ZAP)'} className="text-white/50 hover:text-boosta-yellow disabled:opacity-40" data-testid={`creative-regen-${c.creative_id}`}>
                        <RefreshCw className={`w-3.5 h-3.5 ${regenId===c.creative_id?'animate-spin':''}`}/>
                      </button>
                      <button onClick={()=>del(c.creative_id)} className="text-white/40 hover:text-red-400" data-testid={`creative-delete-${c.creative_id}`}><Trash2 className="w-3.5 h-3.5"/></button>
                    </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      {compareMode && selected.length === 2 && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-40 glass rounded-full px-3 py-2 flex items-center gap-3 border border-boosta-cyan/30 shadow-[0_10px_40px_-10px_rgba(0,240,255,0.4)]" data-testid="compare-action-bar">
          <span className="text-sm text-white/70 pl-2">2 creatives ready</span>
          <button onClick={()=>setCompareOpen(true)} className="btn-primary rounded-full px-5 py-2 text-sm inline-flex items-center gap-2" data-testid="compare-open">
            <GitCompare className="w-4 h-4"/> Compare side by side
          </button>
        </div>
      )}
      {compareOpen && selected.length === 2 && (
        <CompareDialog a={selected[0]} b={selected[1]} onClose={()=>setCompareOpen(false)} onPickWinner={pickWinner} />
      )}
      {publishTarget && <PublishDialog creative={publishTarget} onClose={()=>setPublishTarget(null)} />}
    </div>
  );
}
